import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './StudentResults.css';

const StudentResults = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [assessmentData, setAssessmentData] = useState(null);
    const [badges, setBadges] = useState([]);
    const [conceptPerformance, setConceptPerformance] = useState([]);
    const [metrics, setMetrics] = useState(null);
    const [masteryImpact, setMasteryImpact] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showBadgePopup, setShowBadgePopup] = useState(false);
    const [currentBadge, setCurrentBadge] = useState(null);

    // Get assessment ID from URL query params
    const queryParams = new URLSearchParams(location.search);
    const assessmentId = queryParams.get('assessment_id');

    // Create confetti effect
    const createConfetti = () => {
        const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#ffd700', '#f093fb', '#96e6a1'];
        
        for (let i = 0; i < 50; i++) {
            setTimeout(() => {
                const confetti = document.createElement('div');
                confetti.style.cssText = `
                    position: fixed;
                    width: 10px;
                    height: 10px;
                    background: ${colors[Math.floor(Math.random() * colors.length)]};
                    border-radius: 50%;
                    animation: confetti-fall 3s linear;
                    pointer-events: none;
                    z-index: 9999;
                    left: ${Math.random() * 100}%;
                    top: -10px;
                `;
                document.body.appendChild(confetti);
                
                setTimeout(() => confetti.remove(), 3000);
            }, i * 100);
        }
    };

    // Load assessment details from API
    const loadAssessmentDetails = async () => {
        try {
            const response = await fetch('/api/assessment-results-detailed');
            const data = await response.json();
            
            if (data.success && data.assessment) {
                setAssessmentData(data.assessment);
                return data.assessment;
            } else {
                setError(data.message || 'Assessment not found');
                return null;
            }
        } catch (error) {
            console.error('Error loading assessment details:', error);
            setError('Failed to load assessment results');
            return null;
        }
    };

    // Load earned badges
    const loadEarnedBadges = async () => {
        try {
            const response = await fetch('/api/assessment-badges');
            const data = await response.json();
            
            if (data.success && data.badges && data.badges.length > 0) {
                setBadges(data.badges);
                
                // Show first badge popup after a delay
                setTimeout(() => {
                    if (data.badges.length > 0) {
                        const firstBadge = data.badges[0];
                        setCurrentBadge(firstBadge);
                        setShowBadgePopup(true);
                        setTimeout(() => setShowBadgePopup(false), 5000);
                    }
                }, 1000);
            }
        } catch (error) {
            console.error('Error loading badges:', error);
        }
    };

    // Load concept performance
    const loadConceptPerformance = async () => {
        try {
            const response = await fetch('/api/assessment-concept-performance');
            const data = await response.json();
            
            if (data.success && data.concepts && data.concepts.length > 0) {
                setConceptPerformance(data.concepts);
            }
        } catch (error) {
            console.error('Error loading concept performance:', error);
        }
    };

    // Load streaks and rankings
    const loadStreaksAndRankings = async () => {
        try {
            const response = await fetch('/api/student-success-metrics-enhanced');
            const data = await response.json();
            
            if (data.success) {
                setMetrics(data.metrics);
            }
        } catch (error) {
            console.error('Error loading streaks:', error);
        }
    };

    // Load mastery impact
    const loadMasteryImpact = async () => {
        try {
            const response = await fetch('/api/assessment-mastery-impact');
            const data = await response.json();
            
            if (data.success && data.impact && data.impact.length > 0) {
                setMasteryImpact(data.impact);
            }
        } catch (error) {
            console.error('Error loading mastery impact:', error);
        }
    };

    // Load all data
    useEffect(() => {
        const loadAllData = async () => {
            setLoading(true);
            
            const assessment = await loadAssessmentDetails();
            
            if (assessment) {
                await Promise.all([
                    loadEarnedBadges(),
                    loadConceptPerformance(),
                    loadStreaksAndRankings(),
                    loadMasteryImpact()
                ]);
            }
            
            setLoading(false);
        };
        
        loadAllData();
    }, [assessmentId]);

    // Show confetti for good scores
    useEffect(() => {
        if (assessmentData && assessmentData.score_percentage >= 80) {
            createConfetti();
        }
    }, [assessmentData]);

    const closeBadgePopup = () => {
        setShowBadgePopup(false);
    };

    const getGradeLetter = () => {
        const percentage = assessmentData?.score_percentage || 0;
        if (percentage >= 90) return 'A+';
        if (percentage >= 80) return 'A';
        if (percentage >= 70) return 'B';
        if (percentage >= 60) return 'C';
        if (percentage >= 50) return 'D';
        return 'F';
    };

    const getResultTitle = () => {
        const percentage = assessmentData?.score_percentage || 0;
        if (percentage >= 80) return '🎉 Excellent Work!';
        if (percentage >= 60) return '👍 Good Job!';
        return '💪 Keep Practicing!';
    };

    const getPerformanceMessage = () => {
        const percentage = assessmentData?.score_percentage || 0;
        if (percentage >= 80) {
            return "Outstanding performance! You've shown excellent understanding of the concepts. Keep up the great work!";
        } else if (percentage >= 60) {
            return "Good effort! You're making solid progress. A little more practice will help you master these concepts.";
        } else {
            return "Don't worry! Every great mathematician started somewhere. Keep practicing and you'll improve quickly!";
        }
    };

    if (loading) {
        return (
            <div className="student-results">
                <div className="results-container" style={{ textAlign: 'center', padding: '60px' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '20px' }}>📊</div>
                    <h3>Loading your results...</h3>
                </div>
            </div>
        );
    }

    if (error || !assessmentData) {
        return (
            <div className="student-results">
                <div className="results-container" style={{ textAlign: 'center', padding: '60px' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '20px' }}>⚠️</div>
                    <h3>Unable to Load Results</h3>
                    <p>{error || 'Assessment results not found.'}</p>
                    <div className="action-buttons" style={{ justifyContent: 'center', marginTop: '20px' }}>
                        <a href="/dashboard" className="action-btn btn-primary">
                            <i className="fas fa-home"></i>
                            Back to Dashboard
                        </a>
                        <a href="/assessment" className="action-btn btn-secondary">
                            <i className="fas fa-play"></i>
                            Take New Assessment
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    const formattedDate = assessmentData.completed_at 
        ? new Date(assessmentData.completed_at).toLocaleDateString('en-US', { 
            month: 'long', 
            day: 'numeric', 
            year: 'numeric' 
          }) + ' at ' + new Date(assessmentData.completed_at).toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit' 
          })
        : 'Recently';

    return (
        <div className="student-results">
            <div className="results-container">
                {/* Header */}
                <div className="header">
                    <h1>{getResultTitle()}</h1>
                    <p className="date-text">Completed on {formattedDate}</p>
                </div>

                {/* Score Overview */}
                <div className="score-overview">
                    <div className="score-card">
                        <div className="icon">🎯</div>
                        <div className="value">{assessmentData.score_percentage?.toFixed(1) || 0}%</div>
                        <div className="label">Overall Score</div>
                    </div>
                    
                    <div className="score-card">
                        <div className="icon">✅</div>
                        <div className="value">{assessmentData.correct_answers || 0}/{assessmentData.total_questions || 0}</div>
                        <div className="label">Correct Answers</div>
                    </div>

                    <div className="score-card">
                        <div className="icon">⭐</div>
                        <div className="value">{assessmentData.total_points || 0} pts</div>
                        <div className="label">Points Earned</div>
                    </div>
                </div>

                {/* Badges Earned */}
                {badges.length > 0 && (
                    <div className="badges-section">
                        <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <i className="fas fa-trophy"></i>
                            Badges Earned in This Assessment
                        </h3>
                        <div className="badges-container">
                            {badges.map((badge, index) => (
                                <div 
                                    key={index} 
                                    className="badge-card earned"
                                    onClick={() => {
                                        setCurrentBadge(badge);
                                        setShowBadgePopup(true);
                                        setTimeout(() => setShowBadgePopup(false), 5000);
                                    }}
                                >
                                    <div className="badge-icon">{badge.icon}</div>
                                    <div className="badge-name">{badge.name}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Performance Summary */}
                <div className="performance-summary">
                    <h3 style={{ marginBottom: '15px' }}>
                        <i className="fas fa-chart-line"></i>
                        Performance Summary
                    </h3>
                    <div>
                        <p>{getPerformanceMessage()}</p>
                        <p>
                            <strong>Grade:</strong> 
                            <span style={{ 
                                display: 'inline-block',
                                marginLeft: '8px',
                                padding: '3px 10px',
                                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                color: 'white',
                                borderRadius: '15px',
                                fontSize: '0.9rem'
                            }}>
                                {getGradeLetter()}
                            </span>
                        </p>
                        
                        {/* Additional Stats */}
                        {metrics && (
                            <div style={{ marginTop: '15px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '15px' }}>
                                    <div style={{ textAlign: 'center', padding: '10px', background: '#f8f9fa', borderRadius: '8px' }}>
                                        <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#667eea' }}>{metrics.streak || 0}</div>
                                        <div style={{ fontSize: '0.8rem', color: '#666' }}>Day Streak</div>
                                    </div>
                                    <div style={{ textAlign: 'center', padding: '10px', background: '#f8f9fa', borderRadius: '8px' }}>
                                        <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#28a745' }}>{metrics.accuracy || 0}%</div>
                                        <div style={{ fontSize: '0.8rem', color: '#666' }}>Accuracy</div>
                                    </div>
                                </div>
                                
                                {metrics.rank && (
                                    <div style={{ 
                                        textAlign: 'center', 
                                        padding: '10px', 
                                        background: 'linear-gradient(135deg, #667eea, #764ba2)', 
                                        color: 'white', 
                                        borderRadius: '8px', 
                                        marginTop: '10px' 
                                    }}>
                                        <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>Rank #{metrics.rank}</div>
                                        <div style={{ fontSize: '0.8rem' }}>School Ranking</div>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {/* Mastery Impact */}
                        {masteryImpact.length > 0 && (
                            <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e0e0e0' }}>
                                <p><strong>Mastery Impact:</strong></p>
                                {masteryImpact.map((impact, index) => (
                                    <p key={index} style={{ margin: '5px 0', fontSize: '0.9rem' }}>
                                        {impact.concept}: 
                                        <span style={{ color: impact.change >= 0 ? '#50C878' : '#FF6B9D', marginLeft: '5px' }}>
                                            {impact.change >= 0 ? '+' : ''}{impact.change?.toFixed(1) || 0}%
                                        </span>
                                        <span style={{ color: '#666', marginLeft: '5px' }}>
                                            ({impact.new_mastery?.toFixed(1) || 0}% total)
                                        </span>
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="action-buttons">
                    <a href="/dashboard" className="action-btn btn-primary">
                        <i className="fas fa-home"></i>
                        Back to Dashboard
                    </a>
                    <a href="/assessment" className="action-btn btn-secondary">
                        <i className="fas fa-redo"></i>
                        Try Again
                    </a>
                </div>

                {/* Concept Performance */}
                {conceptPerformance.length > 0 && (
                    <div className="concept-performance">
                        <h3 style={{ marginBottom: '20px' }}>
                            <i className="fas fa-brain"></i>
                            Concept-wise Performance
                        </h3>
                        <div>
                            {conceptPerformance.map((concept, index) => (
                                <div key={index} className="concept-item">
                                    <div>
                                        <strong>{concept.name}</strong>
                                        <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                            {concept.correct}/{concept.total} correct ({concept.percentage}%)
                                        </div>
                                    </div>
                                    <div className="progress-bar">
                                        <div className="progress-fill" style={{ width: `${concept.percentage}%` }}></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Badge Popup */}
            {showBadgePopup && currentBadge && (
                <div className="badge-popup" style={{ display: 'flex' }} onClick={closeBadgePopup}>
                    <div className="badge-popup-content" onClick={(e) => e.stopPropagation()}>
                        <div className="badge-popup-icon">{currentBadge.icon}</div>
                        <h2 className="badge-popup-title">Badge Unlocked!</h2>
                        <div className="badge-popup-name">{currentBadge.name}</div>
                        <div className="badge-popup-desc">{currentBadge.description}</div>
                        <button className="badge-popup-close" onClick={closeBadgePopup}>
                            🎉 Awesome! 🎉
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudentResults;